import { useRef, useState } from "react";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Button from "../ui/Button";
import EmptyModal from "../modals/EmptyModal";
import ReceivingGroupInlineInput from "../forms/receiving/invoices/ReceivingGroupInlineInput";
import { formatUnitOfMeasure } from "../../shared/formatUnitOfMeasure";

function formatCurrency(value) {
  if (value == null || value === "") {
    return "—";
  }
  const normalized = String(value).replace(/,/g, "");
  const amount = parseFloat(normalized);
  if (Number.isNaN(amount)) {
    return `$${value}`;
  }
  return amount.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function formatBarcodeSerial(value) {
  if (value == null || value === "") {
    return "—";
  }
  return String(value);
}

function cartonBarcode(row) {
  return String(row?.barcode ?? "").trim();
}

function parseStockAmount(value) {
  if (value == null || value === "") return null;
  const amount = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : null;
}

export default function InventoryRawMaterialSubTable(props) {
  const { data, onUpdateReceivingItemStock, onPromote, onBarcodeCopied } =
    props;

  const unitLabel = formatUnitOfMeasure(data.unit_of_measure).trim();
  const reasonRequestRef = useRef(null);
  const savingReasonRef = useRef(false);
  const [reason, setReason] = useState("");
  const [savingReason, setSavingReason] = useState(false);
  const [reasonModal, setReasonModal] = useState({
    open: false,
    productName: "",
    nextStock: null,
  });

  const copyBarcode = async (row) => {
    const barcode = cartonBarcode(row);
    if (!barcode) {
      return;
    }

    try {
      await navigator.clipboard.writeText(barcode);
      onBarcodeCopied?.();
    } catch {
      onBarcodeCopied?.(false);
    }
  };

  const finishReasonRequest = (result) => {
    const pending = reasonRequestRef.current;
    reasonRequestRef.current = null;
    savingReasonRef.current = false;
    setSavingReason(false);
    setReason("");
    setReasonModal({ open: false, productName: "", nextStock: null });
    pending?.resolve(result);
  };

  const commitStock = (row, nextStock) => {
    return new Promise((resolve) => {
      reasonRequestRef.current = { resolve, row, nextStock };
      setReason("");
      setReasonModal({
        open: true,
        productName: row.product_name || "carton",
        nextStock,
      });
    });
  };

  const confirmReason = async () => {
    const pending = reasonRequestRef.current;
    const trimmedReason = reason.trim();
    if (!pending || !trimmedReason || savingReasonRef.current) {
      return;
    }

    savingReasonRef.current = true;
    setSavingReason(true);
    const unitCost = parseStockAmount(pending.row.unit_cost) ?? 0;
    const newItemCost = (pending.nextStock * unitCost).toFixed(2);

    try {
      const success = await onUpdateReceivingItemStock(
        pending.row.id,
        pending.nextStock,
        pending.row.old_in_stock ?? pending.row.in_stock,
        pending.row.parent_index,
        pending.row.total_cost,
        newItemCost,
        trimmedReason,
      );
      finishReasonRequest(Boolean(success));
    } catch {
      finishReasonRequest(false);
    }
  };

  const cancelReason = () => {
    if (savingReasonRef.current) {
      return;
    }
    finishReasonRequest("cancel");
  };

  const serialTemplate = (row) => {
    const serial = formatBarcodeSerial(row.barcode_serial);
    const barcode = cartonBarcode(row);

    return (
      <div className="flex items-center gap-1.5">
        <span className="tabular-nums font-mono text-[#0F172A]">{serial}</span>
        {barcode ? (
          <button
            type="button"
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
            title="Copy barcode"
            aria-label={`Copy barcode ${barcode}`}
            onClick={() => copyBarcode(row)}
          >
            <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    );
  };

  const promoteActionTemplate = (row) => {
    const inStock = parseStockAmount(row.in_stock);
    if (inStock == null || inStock <= 0) {
      return null;
    }

    return (
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => onPromote?.(row.id)}
      >
        Promote to sellable stock
      </Button>
    );
  };

  return (
    <div className="operational-embedded-table overflow-hidden rounded-lg border border-[#E2E8F0] bg-white">
      <DataTable
        value={data.subItems}
        className="p-datatable-sm text-sm"
        dataKey="id"
      >
        <Column
          field="supplier_name"
          header="Supplier Name"
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="invoice_number"
          header="Invoice Number"
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="product_name"
          header="Product Name"
          style={{ minWidth: "12rem" }}
          bodyClassName="font-medium text-[#0F172A]"
        />
        <Column
          header="Serial"
          body={serialTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Unit Cost"
          alignHeader="right"
          body={(row) => (
            <span className="tabular-nums">
              {formatCurrency(row.unit_cost)}
            </span>
          )}
          style={{ minWidth: "7rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column
          header="Total Unit"
          alignHeader="right"
          body={(row) => (
            <ReceivingGroupInlineInput
              ariaLabel={`Total units for ${row.product_name || "carton"}`}
              value={parseStockAmount(row.in_stock)}
              suffix={unitLabel || undefined}
              commitOnBlur
              onCommit={(value) => commitStock(row, value)}
            />
          )}
          style={{ minWidth: "11rem", width: "11rem", textAlign: "right" }}
          bodyClassName="overflow-visible text-right tabular-nums"
        />
        <Column
          header="Total Cost"
          alignHeader="right"
          body={(row) => (
            <span className="tabular-nums font-medium text-[#0F172A]">
              {formatCurrency(row.total_cost)}
            </span>
          )}
          style={{ minWidth: "7rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column
          header="Actions"
          body={promoteActionTemplate}
          style={{ minWidth: "12rem" }}
        />
      </DataTable>
      <EmptyModal
        open={reasonModal.open}
        setOpen={(next) => {
          if (!next.open) {
            cancelReason();
          }
        }}
        childComponent={
          <form
            className="text-left"
            onSubmit={(event) => {
              event.preventDefault();
              void confirmReason();
            }}
          >
            <h3 className="text-base font-medium text-[#0F172A]">
              Reason for stock change
            </h3>
            <p className="mt-2 text-sm text-[#64748B]">
              {reasonModal.productName} will be updated to{" "}
              {reasonModal.nextStock}
              {unitLabel ? ` ${unitLabel}` : ""}. This reason is stored on the
              barcode history.
            </p>
            <label
              htmlFor="inventory-stock-reason"
              className="mt-4 block text-sm font-medium text-[#0F172A]"
            >
              Reason
            </label>
            <textarea
              id="inventory-stock-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              required
              autoFocus
              className="mt-1 block w-full rounded-xl border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#0F172A] shadow-sm placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:outline-none focus:ring-2 focus:ring-[#BBF7D0]"
              placeholder="Why is this quantity changing?"
            />
            <div className="mt-5 flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={cancelReason}
                disabled={savingReason}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={savingReason}
                disabled={!reason.trim() || savingReason}
              >
                Save
              </Button>
            </div>
          </form>
        }
      />
    </div>
  );
}
